import { APP_BASE_HREF, LocationStrategy } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { LocationService } from '@callrail/looky/util';

import { BaseHostService } from '../base-host/base-host.service';

/**
 * Used to help with links for the credit card page, keeping in mind
 * that only certain URLs are valid in order to make the credit card form work
 */
@Injectable({
  providedIn: 'root',
})
export class UrlHelperService {
  constructor(
    private locationService: LocationService,
    private baseHostService: BaseHostService,
    private locationStrategy: LocationStrategy,
    @Optional() private router: Router,
    @Optional() @Inject(APP_BASE_HREF) private baseHref?: string, // from callrail apps
    @Optional() @Inject('env') private env?: any, // from Looky apps
    @Optional() @Inject('railsEnv') private railsEnv?: any // from callrail apps
  ) {}

  // the credit card form is an iFrame that points to zuora,
  // but requests from white label domains are blocked by zuora
  // so, we can only use routerLinks when the user is already on
  // app.callrail.com (or the regular base-host for the env)

  /**
   * Whether or not you can safely use a router link to get to the host specified
   * Using router links is preferable over hrefs because hrefs will cause the app to reload during routing
   */
  public canUseRouterLink(host: string): boolean {
    // locally, baseHost will (probably) never match this.locationService.host by default
    // edit your environment variables locally so that this matches if that's important to you
    return (
      this.locationService.host().toLowerCase() === host.toLowerCase() &&
      !!this.router
    );
  }

  /**
   * Whether or not you can use angular router to get to the path specified from
   * your current page
   * @param absolutePath the full absolute path you want to go to. Should start with a slash `/`
   */
  public canRouteToPath(absolutePath: string): boolean {
    // this makes a potentially dangerous assumption that no baseHref is a subString of another baseHref
    // this could be a problem because '/' is a substring of '/v2/a/:id' and '/account-center'
    // however, '/' is the baseHref of v1 and superadmin, which also happen to be apps without a Router provider
    // because of that fact alone, this is not an issue RIGHT NOW.
    // This WOULD be an issue if an app hosted at base href '/' provided a Router and was able to use its own routing
    return !!absolutePath?.match(this.baseHrefRegex) && !!this.router;
  }

  public getRouterPathFromAbsolute(absolutePath: string): string {
    const noBaseHrefPath = absolutePath.replace(this.baseHrefRegex, '');
    // add a leading '/' if there wasn't one already
    return `/${noBaseHrefPath}`.replace('//', '/');
  }

  /**
   * Useful to use in the typescript. Pass in a full href absolute path
   * e.g. `/v2/a/123456/profile/edit` and this method will move the browser there.
   * If it can use a router to get there, it will. If it can't, then it will hard link there.
   * @deprecated in favor of `crossAppLink`. Functionally identical, just better naming
   */
  public navigateToPath(absolutePath: string): void {
    this.crossAppLink(absolutePath);
  }

  /**
   * Useful to use in the typescript. Pass in a full href absolute path
   * e.g. `/v2/a/123456/profile/edit` and this method will move the browser there.
   * If it can use a router to get there, it will. If it can't, then it will hard link there.
   */
  public crossAppLink(absolutePath: string): void {
    if (this.canRouteToPath(absolutePath)) {
      this.router.navigateByUrl(this.getRouterPathFromAbsolute(absolutePath));
    } else {
      // dev environments don't usually share a base URL (localhost:900x vs callrail.test)
      // so we need a way to jump back from an app base host to the monolith base host
      if (
        (this.env && this.env.development) ||
        (this.railsEnv && this.railsEnv.development)
      ) {
        const url = this.buildUrl(this.baseHostService.baseHost, absolutePath);
        this.locationService.go(url, true);
      } else {
        this.locationService.go(absolutePath, true);
      }
    }
  }

  public getCrossAppLinkUrl(absolutePath: string): string {
    if (this.canRouteToPath(absolutePath)) {
      return this.getRouterPathFromAbsolute(absolutePath);
    } else {
      // dev environments don't usually share a base URL (localhost:900x vs callrail.test)
      // so we need a way to jump back from an app base host to the monolith base host
      if (
        (this.env && this.env.development) ||
        (this.railsEnv && this.railsEnv.development)
      ) {
        return this.buildUrl(this.baseHostService.baseHost, absolutePath);
      } else {
        return absolutePath;
      }
    }
  }

  public get canRouteToBaseHost() {
    return this.canUseRouterLink(this.baseHostService.baseHost);
  }

  /**
   * Goes to the url path specified, but with the base host. This method's api is the same as
   * Angular's `Router.navigate()`. It's preferred to use the BaseHostGuard on a route if your
   * goal is just to have a certain route always be on the base host for an environment.
   * https://github.com/angular/angular/blob/main/packages/router/src/router.ts#L1015
   */
  public navigateBaseHost(
    commands: any[],
    extras: NavigationExtras = { skipLocationChange: false }
  ) {
    if (!this.router) {
      throw new Error(
        `Can't use router navigation because this app doesn't provide a Router!`
      );
    }
    if (this.canRouteToBaseHost) {
      this.router.navigate(commands, extras);
    } else {
      const serializedUrl = this.router.serializeUrl(
        this.router.createUrlTree(commands, extras)
      );
      const relativePath =
        this.locationStrategy.prepareExternalUrl(serializedUrl);
      const url = this.buildUrl(this.baseHostService.baseHost, relativePath);
      this.locationService.go(url, true);
    }
  }

  public buildUrl(host: string, pathAndExtras: string): string {
    const protocolAndHost = `${this.locationService.protocol}//${host}`;
    if (pathAndExtras?.startsWith(protocolAndHost)) {
      return pathAndExtras;
    }
    return `${protocolAndHost}${pathAndExtras}`;
  }

  // returns 'app.callrail.com' given a string 'https://app.callrail.com/'
  public domain(url: string): string {
    const matches = (url || '').match(/(.*\/\/)([^\/]+)/);
    if (!matches) {
      return '';
    }
    return matches ? matches[2] : '';
  }

  private get baseHrefRegex() {
    // instead of matching literally to ':id', match any number of numeric characters
    let basePath = this.baseHref || '/';

    // START TEMPORARY CODE - TO BE REMOVED ONCE V2 ROUTES ARE GONE
    // TEMPORARY, treat /analytics/a/ and /v2/a/ as the same paths
    // this means, when you're on /analytics/a/* and click a cross-app-link for /v2/a*
    // then the app will assume that it can use the router and will send you to /analytics/a/* instead.
    // End result: if you're in the analytics looky app, you'll stay in the analytics looky app.
    basePath = basePath.replace(/analytics|v2/, '(analytics|v2)');
    // END TEMPORARY CODE

    // since the base href includes a trailing slash, we don't want to necessarily match that
    // because /v2/a/12345 should be a match for the base href /v2/a/:id/
    // but it wouldn't match because /v2/a/12345 doen't have a trailing slash
    basePath =
      basePath.length > 1 && basePath.endsWith('/')
        ? basePath.slice(0, -1)
        : basePath;
    // regex explanation:
    // ^ the match needs to start at the beginning of the string
    // ${basepath} needs to match character for character what the basepath is (unless it had :id)
    // (?=(/|$)) positive lookahead: the character after the base path needs to be a slash or the end of the string
    // the last part prevents '/account-center' from matching the url '/account-center-info/something'
    return new RegExp(`^${basePath}(?=(/|$))`);
  }
}
